import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Button, Heading, Text } from '@afterpaytouch/core'
import { updateAccountDetails, useAppDispatch, useCheckoutRegistrationAddress, useConsumerSelector, useCountry } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { DisabledModal, FormWrapper } from '..'
import FormBuilder from '@afterpaytouch/form'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { DocumentProps } from '../../model'
import { getSchema, getVerifyAddressPayload } from './utils'
import { Wrapper } from '../Wrapper'
import { VerifyAddressFormData } from './types'
import { ErrorMessage } from '../VerifyIdentity/components'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { isCallable } from '@afterpay/utils'

export const VerifyAddress: FunctionComponent<DocumentProps> = ({ onSuccess, onFailure }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [lock, setLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const { buttonSize, headingSize, textSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const consumer = useConsumerSelector()
  const initialAddresses = useCheckoutRegistrationAddress() // Are we using this or consumer address?
  const country = useCountry()

  const { schema } = useMemo(() => {
    const components = {
      submit: () => (
        <Button.Primary loading={lock} size={buttonSize} type='submit' disabled={lock} padding='Fluid'>
          {t('verifyIdentity:partialVerified:cta')}
        </Button.Primary>
      ),
    }
    return getSchema(components, t, initialAddresses, country)
  }, [initialAddresses, t, country, buttonSize, lock])

  const onSubmit = async (data: VerifyAddressFormData): Promise<void> => {
    setError(null)
    logEvent(TrackingEvent.PRESSED_SOFTID_LANDING_PAGE_ADDRESS_VERIFY_BUTTON)
    setLock(true)
    const payload = getVerifyAddressPayload(consumer, data, country)

    try {
      await dispatch(updateAccountDetails(payload)).unwrap()
      if (isCallable(onSuccess)) {
        onSuccess()
      }
    } catch (e) {
      const error = getIdentityVerifyErrorCode(e)
      if ([ErrorCode.MaxAttemptsReached, ErrorCode.MaxAttemptsReachedForType].includes(e?.data?.errorCode)) {
        if (isCallable(onFailure)) {
          onFailure()
        }
        return
      }
      setError(error ?? IdentityErrorCode.Default)
      setLock(false)
    }
  }

  const onError = (data: string): void => {
    console.log('error', data)
    // setError(data)
  }

  useEffect(() => {
    if (typeof error !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error])

  return (
    <>
      <Heading size={headingSize}>{t('verifyIdentity:partialVerified:address:heading')}</Heading>
      <Wrapper marginType='Small'>
        <Text size={textSize}>{t('verifyIdentity:partialVerified:address:description')}</Text>
      </Wrapper>
      <ErrorMessage identityType='licence' error={error} />
      <FormWrapper>
        <MaskedElement actionName='Address Verification Form'>
          <FormBuilder schema={schema} testNameSpace={'verify-address-page'} onSubmit={onSubmit} onError={onError} disabled={lock} />
        </MaskedElement>
      </FormWrapper>
      {lock && <DisabledModal />}
    </>
  )
}
